// src/components/CoverPage/TokenSaleSection.jsx
import React, { useState, useEffect } from "react";
import { ethers } from "ethers";
import Button from "./Button";
import TokenSalePopup from "../TokenSalePopup";
import AddTokenReminderPopup from "../AddTokenReminderPopup";
import "./TokenSaleSection.css";
import { YILDX_TOKEN_ADDRESS, YILDX_TOKEN_ABI } from "../../constants/contractInfo";
import TokenSaleAdmin from "./TokenSaleAdmin";
import mailingListStore from '../../services/mailingListStore';

const TokenSaleSection = ({ toggleWalletModal, web3Connected, provider, account }) => {
  // State for token sale data
  const [tokenData, setTokenData] = useState({
    totalSupply: 0,
    tokensForSale: 0,
    tokensSold: 0,
    maxPurchase: 0,
    initialSaleActive: false,
    estimatedUsdPrice: 0
  });
  
  // State for tier data
  const [tiers, setTiers] = useState([]);
  const [currentTier, setCurrentTier] = useState(0);
  const [currentPrice, setCurrentPrice] = useState("0");
  const [tokensUntilNextTier, setTokensUntilNextTier] = useState(0);

  // State for loading and refreshing status
  const [initialLoading, setInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // State for purchase amount and ETH calculations
  const [purchaseAmount, setPurchaseAmount] = useState("");
  const [ethAmount, setEthAmount] = useState("");
  const [inputMode, setInputMode] = useState("token"); // "token" or "eth"
  
  // State for purchase processing
  const [isPurchasing, setIsPurchasing] = useState(false);
  
  // Transaction popup states
  const [showTransactionPopup, setShowTransactionPopup] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState('pending'); // 'pending', 'success', 'failed', 'cancelled'
  const [transactionMessage, setTransactionMessage] = useState('');
  const [transactionHash, setTransactionHash] = useState('');
  const [transactionStartTime, setTransactionStartTime] = useState(0);
  
  // Token reminder popup state
  const [showTokenReminder, setShowTokenReminder] = useState(false);
  
  // Mailing list popup state
  const [showMailingListPopup, setShowMailingListPopup] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');
  
  // Owner state
  const [isOwner, setIsOwner] = useState(false);
  
  // Initialize contract
  const [tokenContract, setTokenContract] = useState(null);
  
  // Effect to initialize contract
  useEffect(() => {
    const initContract = async () => {
      try {
        // Try to connect to provider from web3 wallet first
        let contractInstance;
        
        if (provider && account) {
          const signer = provider.getSigner();
          contractInstance = new ethers.Contract(YILDX_TOKEN_ADDRESS, YILDX_TOKEN_ABI, signer);
          console.log("Token contract initialized with signer:", contractInstance.address);
        } else {
          // Fall back to local provider for read-only access
          const localProvider = new ethers.providers.JsonRpcProvider("http://localhost:8545");
          contractInstance = new ethers.Contract(YILDX_TOKEN_ADDRESS, YILDX_TOKEN_ABI, localProvider);
          console.log("Token contract initialized with local provider:", contractInstance.address);
        }
        
        setTokenContract(contractInstance);
      } catch (error) {
        console.error("Error initializing contracts:", error);
      }
    };
    
    initContract();
  }, [provider, account]);
  
  // Add this effect to check if current user is the owner
  useEffect(() => {
    const checkOwnership = async () => {
      if (tokenContract && account) {
        try {
          const ownerAddress = await tokenContract.owner();
          const isUserOwner = ownerAddress.toLowerCase() === account.toLowerCase();
          setIsOwner(isUserOwner);
        } catch (error) {
          console.error("Error checking ownership:", error);
        }
      }
    };
    
    checkOwnership();
  }, [tokenContract, account]);
  
  // Effect to fetch token sale data
  useEffect(() => {
    const fetchData = async () => {
      if (tokenContract) {
        // Keep initialLoading true only for the very first load
        // Otherwise just set isRefreshing for subsequent updates
        if (!initialLoading) {
          setIsRefreshing(true);
        }
        
        try {
          console.log("Fetching token sale data from contract...");
          
          // First the basic data
          const basicsPromises = [
            tokenContract.totalSupply(),
            tokenContract.initialSaleActive ? tokenContract.initialSaleActive() : false,
          ];
          
          // Get the results for the basic data
          const [totalSupply, initialSaleActive] = await Promise.all(basicsPromises);
          
          // Now try to get the rest of the data
          try {
            // Try to get the sale status method first
            const saleStatus = await tokenContract.getSaleStatus();
            
            console.log("Sale status:", {
              isActive: saleStatus.isActive,
              currentPrice: saleStatus.currentPrice.toString(),
              currentTier: saleStatus.currentTier.toNumber(),
              sold: ethers.utils.formatEther(saleStatus.sold),
              remaining: ethers.utils.formatEther(saleStatus.remaining),
              nextTierThreshold: ethers.utils.formatEther(saleStatus.nextTierThreshold)
            });
            
            // Get all price tiers
            const allTiers = await tokenContract.getAllPriceTiers();
            const tiersList = [];
            
            for (let i = 0; i < allTiers.thresholds.length; i++) {
              tiersList.push({
                threshold: parseFloat(ethers.utils.formatEther(allTiers.thresholds[i])),
                price: allTiers.prices[i].toString()
              });
            }
            
            console.log("Price tiers:", tiersList);
            
            // Try to get estimated USD price if available
            let estimatedUsdPrice = 0;
            try {
              estimatedUsdPrice = await tokenContract.getEstimatedUsdPrice();
            } catch (error) {
              console.log("Estimated USD price not available:", error.message);
            }
            
            // Update state with the fetched data
            setTokenData({
              totalSupply: parseFloat(ethers.utils.formatEther(totalSupply)),
              tokensForSale: parseFloat(ethers.utils.formatEther(saleStatus.sold.add(saleStatus.remaining))),
              tokensSold: parseFloat(ethers.utils.formatEther(saleStatus.sold)),
              maxPurchase: parseFloat(ethers.utils.formatEther(saleStatus.remaining)),
              initialSaleActive: saleStatus.isActive,
              estimatedUsdPrice: estimatedUsdPrice.toString()
            });
            
            setTiers(tiersList);
            setCurrentTier(saleStatus.currentTier.toNumber());
            setCurrentPrice(saleStatus.currentPrice.toString());
            setTokensUntilNextTier(parseFloat(ethers.utils.formatEther(saleStatus.nextTierThreshold)));
            
          } catch (error) {
            console.log("getSaleStatus is not available, falling back to individual calls:", error.message);
            
            // Try individual methods (older contract version)
            try {
              // Get tokens sold and tokens for sale
              const [
                initialSaleAmount,
                tokensSold,
                initialSaleActive2 // This might override the earlier value if it exists as a direct method
              ] = await Promise.all([
                tokenContract.INITIAL_SALE_AMOUNT(),
                tokenContract.tokensSold(),
                // Try again with the method directly (different contract versions)
                tokenContract.initialSaleActive ? tokenContract.initialSaleActive() : initialSaleActive
              ]);
              
              // Get all price tiers
              const allTiers = await tokenContract.getAllPriceTiers();
              const tiersList = [];
              
              for (let i = 0; i < allTiers.thresholds.length; i++) {
                tiersList.push({
                  threshold: parseFloat(ethers.utils.formatEther(allTiers.thresholds[i])),
                  price: allTiers.prices[i].toString()
                });
              }
              
              // Get current tier and price
              const [currentTierValue, currentPriceValue] = await Promise.all([
                tokenContract.getCurrentTier(),
                tokenContract.getCurrentPrice()
              ]);
              
              // Calculate tokens remaining for sale
              const tokensRemaining = initialSaleAmount.sub(tokensSold);
              
              console.log("Token data fetched via individual calls:", {
                totalSupply: ethers.utils.formatEther(totalSupply),
                tokensForSale: ethers.utils.formatEther(initialSaleAmount),
                tokensSold: ethers.utils.formatEther(tokensSold),
                tokensRemaining: ethers.utils.formatEther(tokensRemaining),
                currentPrice: currentPriceValue.toString(),
                initialSaleActive: initialSaleActive2,
                tiers: tiersList,
                currentTier: currentTierValue.toNumber()
              });
              
              // Update state
              setTokenData({
                totalSupply: parseFloat(ethers.utils.formatEther(totalSupply)),
                tokensForSale: parseFloat(ethers.utils.formatEther(initialSaleAmount)),
                tokensSold: parseFloat(ethers.utils.formatEther(tokensSold)),
                maxPurchase: parseFloat(ethers.utils.formatEther(tokensRemaining)),
                initialSaleActive: initialSaleActive2,
              });
              
              setTiers(tiersList);
              setCurrentTier(currentTierValue.toNumber());
              setCurrentPrice(currentPriceValue.toString());
              
              // Try to calculate tokens until next tier
              if (currentTierValue.toNumber() < tiersList.length - 1) {
                const nextTierThreshold = tiersList[currentTierValue.toNumber() + 1].threshold;
                const tokensUntilNext = nextTierThreshold - parseFloat(ethers.utils.formatEther(tokensSold));
                setTokensUntilNextTier(tokensUntilNext);
              } else {
                setTokensUntilNextTier(0);
              }
              
            } catch (error) {
              console.error("Error with individual calls, using minimal data:", error);
              
              // Minimal approach when tier methods aren't available
              try {
                const [initialSaleAmount, tokensSold] = await Promise.all([
                  tokenContract.INITIAL_SALE_AMOUNT(),
                  tokenContract.tokensSold(),
                ]);
                
                // Calculate tokens remaining for sale
                const tokensRemaining = initialSaleAmount.sub(tokensSold);
                
                console.log("Minimal token data fetched:", {
                  totalSupply: ethers.utils.formatEther(totalSupply),
                  tokensForSale: ethers.utils.formatEther(initialSaleAmount),
                  tokensSold: ethers.utils.formatEther(tokensSold),
                  tokensRemaining: ethers.utils.formatEther(tokensRemaining),
                  initialSaleActive
                });
                
                // Update state with minimal data
                setTokenData({
                  totalSupply: parseFloat(ethers.utils.formatEther(totalSupply)),
                  tokensForSale: parseFloat(ethers.utils.formatEther(initialSaleAmount)),
                  tokensSold: parseFloat(ethers.utils.formatEther(tokensSold)),
                  maxPurchase: parseFloat(ethers.utils.formatEther(tokensRemaining)),
                  initialSaleActive,
                });
              } catch (error) {
                console.error("Failed to fetch even minimal data:", error);
                // Use the basic data we already have
                setTokenData({
                  totalSupply: parseFloat(ethers.utils.formatEther(totalSupply)),
                  tokensForSale: 0,
                  tokensSold: 0,
                  maxPurchase: 0,
                  initialSaleActive,
                });
              }
            }
          }
          
        } catch (error) {
          console.error("Error fetching token sale data:", error);
          console.log("Failed to fetch token data:", error.message);
        } finally {
          // Add a slight delay before removing states for visual feedback
          setTimeout(() => {
            setInitialLoading(false);
            setIsRefreshing(false);
          }, 300);
        }
      }
    };
    
    fetchData();
    
    // Set up interval to refresh data every 30 seconds
    const interval = setInterval(() => {
      if (tokenContract) {
        // Only set refreshing state for interval updates, never show full loading spinner
        setIsRefreshing(true);
        fetchData();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [tokenContract, initialLoading, isOwner]);
  
  // Toggle between token and ETH input modes
  const toggleInputMode = () => {
    if (inputMode === "token") {
      setInputMode("eth");
      // Clear the token input and focus on ETH
      setPurchaseAmount("");
    } else {
      setInputMode("token");
      // Clear the ETH input and focus on token
      setEthAmount("");
    }
  };
  
  // Helper to format ETH price for display
  const formatEthPrice = (priceInWei) => {
    if (!priceInWei) return "0 ETH";
    
    const ethPrice = ethers.utils.formatEther(priceInWei);
    const numericValue = parseFloat(ethPrice);
    
    // Format based on size
    if (numericValue >= 1) {
      return `${numericValue.toFixed(2)} ETH`;
    } else if (numericValue >= 0.01) {
      return `${numericValue.toFixed(4)} ETH`;
    } else {
      return `${numericValue.toFixed(6)} ETH`;
    }
  };
  
  // Helper to get current tier name
  const getCurrentTierName = () => {
    if (tiers.length === 0) return "Tier 1";
    return `Tier ${currentTier + 1}`;
  };
  
  // Calculate ETH cost for token amount based on current tier and potential tier crossover
  const calculateEthForTokens = async (tokenAmount) => {
    if (!tokenAmount || !tokenContract) return "0";
    
    try {
      // Convert to token units (wei equivalent)
      const tokenUnits = ethers.utils.parseEther(tokenAmount);
      
      // This is a simplified approximation - actual calculation would need to handle tier crossover
      // For exact calculation we would need a contract method that does multi-tier calculation
      // For now, we'll use a simple approximation based on current price
      const ethCost = (window.BigInt(tokenUnits.toString()) * window.BigInt(currentPrice)) / window.BigInt(10**18);
      return ethers.utils.formatEther(ethCost.toString());
    } catch (error) {
      console.error("Error calculating ETH cost:", error);
      return "0";
    }
  };
  
  // Calculate token amount for ETH based on current tier and potential tier crossover
  const calculateTokensForEth = async (ethValue) => {
    if (!ethValue || !tokenContract) return "0";
    
    try {
      // Convert to wei
      const weiAmount = ethers.utils.parseEther(ethValue);
      
      // This is a simplified approximation - actual calculation would need to handle tier crossover
      // For exact calculation we would need a contract method that does multi-tier calculation
      // For now, we'll use a simple approximation based on current price
      const tokenAmount = (window.BigInt(weiAmount.toString()) * window.BigInt(10**18)) / window.BigInt(currentPrice);
      return ethers.utils.formatEther(tokenAmount.toString());
    } catch (error) {
      console.error("Error calculating token amount:", error);
      return "0";
    }
  };
  
  // Handle input change based on current mode
  const handleInputChange = async (e) => {
    const value = e.target.value;
    
    if (inputMode === "token") {
      setPurchaseAmount(value);
      if (value) {
        const ethCost = await calculateEthForTokens(value);
        setEthAmount(ethCost);
      } else {
        setEthAmount("");
      }
    } else {
      setEthAmount(value);
      if (value) {
        const tokenAmount = await calculateTokensForEth(value);
        setPurchaseAmount(tokenAmount);
      } else {
        setPurchaseAmount("");
      }
    }
  };
  
  // Handle token reminder completion
  const handleTokenReminderComplete = () => {
    console.log("Token reminder process completed");
    setShowTokenReminder(false);
    // Reset purchase amount for next purchase
    setPurchaseAmount("");
    setEthAmount("");
  };
  
  // Handle purchase
  const handlePurchase = async () => {
    if (!web3Connected) {
      toggleWalletModal();
      return;
    }
    
    // Validate purchase amount
    const amount = parseFloat(purchaseAmount);
    if (isNaN(amount) || amount <= 0 || amount > tokenData.maxPurchase) {
      alert(`Please enter a valid amount (maximum: ${tokenData.maxPurchase})`);
      return;
    }
    
    // Check if sale is active
    if (!tokenData.initialSaleActive) {
      alert("Token mint is not currently active");
      return;
    }
    
    try {
      // Purchase tokens with ETH
      setIsPurchasing(true);
      
      // Prepare transaction popup
      setTransactionStatus('pending');
      setTransactionMessage('Minting YILDX tokens with ETH. Please confirm in your wallet.');
      setTransactionHash('');
      setTransactionStartTime(Date.now());
      setShowTransactionPopup(true);
      
      console.log(`Attempting to mint ${purchaseAmount} YILDX tokens with ${ethAmount} ETH...`);
      
      // Calculate minimum tokens expected (95% of calculated amount for slippage protection)
      const minTokensExpected = ethers.utils.parseEther(
        (parseFloat(purchaseAmount) * 0.95).toString()
      );
      
      // Send ETH to buy tokens with slippage protection
      const tx = await tokenContract.buyTokens(
        minTokensExpected,
        { value: ethers.utils.parseEther(ethAmount) }
      );
      
      console.log("Purchase transaction submitted:", tx.hash);
      
      // Update transaction hash
      setTransactionHash(tx.hash);
      setTransactionMessage('Token purchase submitted to the blockchain and waiting for confirmation.');
      
      // Wait for transaction to be mined
      await tx.wait();
      console.log("Purchase transaction confirmed");
      
      // Update popup to success
      setTransactionStatus('success');
      setTransactionMessage(`Successfully purchased approximately ${purchaseAmount} YILDX tokens! You can now add the token to your wallet.`);
      
      // Refresh data
      await refreshTokenSaleData();
      
    } catch (error) {
      console.error("Error purchasing tokens:", error);
      
      // Check if user rejected
      if (error.code === 4001 || error.message?.includes('user rejected')) {
        setTransactionStatus('cancelled');
        setTransactionMessage('You cancelled the token purchase transaction.');
      } else {
        setTransactionStatus('failed');
        setTransactionMessage('Failed to purchase tokens: ' + (error.message || 'Unknown error'));
      }
    } finally {
      setIsPurchasing(false);
    }
  };
  
  // Helper function to refresh token sale data
  const refreshTokenSaleData = async () => {
    if (!tokenContract) return;
    
    setIsRefreshing(true);
    try {
      // Try to get the sale status method first
      try {
        const saleStatus = await tokenContract.getSaleStatus();
        
        // Update state with the fetched data
        setTokenData(prev => ({
          ...prev,
          tokensSold: parseFloat(ethers.utils.formatEther(saleStatus.sold)),
          maxPurchase: parseFloat(ethers.utils.formatEther(saleStatus.remaining)),
          initialSaleActive: saleStatus.isActive
        }));
        
        setCurrentTier(saleStatus.currentTier.toNumber());
        setCurrentPrice(saleStatus.currentPrice.toString());
        setTokensUntilNextTier(parseFloat(ethers.utils.formatEther(saleStatus.nextTierThreshold)));
      } catch (error) {
        console.log("getSaleStatus is not available for refresh, using individual calls:", error.message);
        
        // Try individual methods
        try {
          const [
            initialSaleAmount,
            tokensSold,
            initialSaleActive,
            currentTierValue,
            currentPriceValue
          ] = await Promise.all([
            tokenContract.INITIAL_SALE_AMOUNT(),
            tokenContract.tokensSold(),
            tokenContract.initialSaleActive(),
            tokenContract.getCurrentTier(),
            tokenContract.getCurrentPrice()
          ]);
          
          // Calculate tokens remaining for sale
          const tokensRemaining = initialSaleAmount.sub(tokensSold);
          
          // Update state with fetched data
          setTokenData(prev => ({
            ...prev,
            tokensSold: parseFloat(ethers.utils.formatEther(tokensSold)),
            maxPurchase: parseFloat(ethers.utils.formatEther(tokensRemaining)),
            initialSaleActive
          }));
          
          setCurrentTier(currentTierValue.toNumber());
          setCurrentPrice(currentPriceValue.toString());
          
          // If tiers exist, calculate tokens until next tier
          if (tiers.length > 0 && currentTierValue.toNumber() < tiers.length - 1) {
            const nextTierThreshold = tiers[currentTierValue.toNumber() + 1].threshold;
            const tokensUntilNext = nextTierThreshold - parseFloat(ethers.utils.formatEther(tokensSold));
            setTokensUntilNextTier(tokensUntilNext);
          } else {
            setTokensUntilNextTier(0);
          }
        } catch (error) {
          console.error("Error refreshing with individual calls:", error);
          
          // Minimal refresh
          try {
            const [initialSaleAmount, tokensSold, initialSaleActive] = await Promise.all([
              tokenContract.INITIAL_SALE_AMOUNT(),
              tokenContract.tokensSold(),
              tokenContract.initialSaleActive()
            ]);
            
            // Calculate tokens remaining for sale
            const tokensRemaining = initialSaleAmount.sub(tokensSold);
            
            // Update state with minimal data
            setTokenData(prev => ({
              ...prev,
              tokensSold: parseFloat(ethers.utils.formatEther(tokensSold)),
              maxPurchase: parseFloat(ethers.utils.formatEther(tokensRemaining)),
              initialSaleActive
            }));
          } catch (error) {
            console.error("Failed to refresh even minimal data:", error);
          }
        }
      }
      
    } catch (error) {
      console.error("Error refreshing token sale data:", error);
    } finally {
      setTimeout(() => {
        setIsRefreshing(false);
      }, 300);
    }
  };
  
  // Function to handle successful purchase completion and show token reminder
  const handlePurchaseComplete = (tokenAddress) => {
    console.log("Purchase completed, showing token reminder for address:", tokenAddress);
    setShowTransactionPopup(false);
    setShowTokenReminder(true);
  };
  
  // Handle email subscription
const handleSubscribeToMailingList = async (e) => {
  e.preventDefault();
  
  if (!email) {
    setSubscribeError('Please enter a valid email address');
    return;
  }
  
  setIsSubscribing(true);
  setSubscribeError('');
  
  try {
    // Use the store instead of direct fetch
    const result = await mailingListStore.subscribeEmail(email, name, 'website');
    
    if (result.success) {
      console.log('Subscription successful:', result.message);
      setSubscribeSuccess(true);
      setEmail('');
      setName('');
      
      // Close popup after 3 seconds
      setTimeout(() => {
        setShowMailingListPopup(false);
        setSubscribeSuccess(false);
      }, 3000);
    } else if (result.exists) {
      // Already subscribed is a success case for the UI
      console.log('User already subscribed:', result.message);
      setSubscribeSuccess(true);
      setEmail('');
      setName('');
      
      // Close popup after 3 seconds
      setTimeout(() => {
        setShowMailingListPopup(false);
        setSubscribeSuccess(false);
      }, 3000);
    } else {
      throw new Error(result.message);
    }
  } catch (error) {
    console.error('Error subscribing to mailing list:', error);
    setSubscribeError(error.message || 'Failed to subscribe. Please try again later.');
  } finally {
    setIsSubscribing(false);
  }
};
  
  // Open mailing list popup
  const openMailingListPopup = () => {
    setShowMailingListPopup(true);
    setSubscribeSuccess(false);
    setSubscribeError('');
  };
  
  // Calculate percentages for tier progress bars
  const calculateTierPercentages = () => {
    if (tiers.length === 0) return [];
    
    const result = [];
    let prevThreshold = 0;
    
    for (let i = 0; i < tiers.length; i++) {
      const tierSize = tiers[i].threshold - prevThreshold;
      const soldInTier = Math.min(Math.max(tokenData.tokensSold - prevThreshold, 0), tierSize);
      const percentageInTier = (soldInTier / tierSize) * 100;
      
      result.push({
        tierNumber: i + 1,
        startToken: prevThreshold,
        endToken: tiers[i].threshold,
        tierSize,
        soldInTier,
        percentageInTier,
        price: formatEthPrice(tiers[i].price),
        isActive: tokenData.tokensSold >= prevThreshold && tokenData.tokensSold < tiers[i].threshold
      });
      
      prevThreshold = tiers[i].threshold;
    }
    
    return result;
  };
  
  // Get formatted data for tier display
  const tierProgressData = calculateTierPercentages();
  
  // Calculate percentages for the overall progress bar
  const soldPercentage = tokenData.tokensForSale > 0 ? 
    (tokenData.tokensSold / tokenData.tokensForSale) * 100 : 0;
  const remainingPercentage = 100 - soldPercentage;

  // Render inactive sale view (simplified)
  const renderInactiveSaleView = () => {
    return (
      <div className="token-info-card">
        <div className="token-info-header">
          <h3 className="text-2xl font-semibold mb-6">Token Information</h3>
          <div className="token-symbol">YILDX</div>
        </div>

        {initialLoading ? (
          <div className="loading-spinner flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
          </div>
        ) : (
          <>
            <div className="token-stats">
              <div className="stat-item">
                <span className="stat-label">Total Supply</span>
                <div className="stat-value-container">
                  <span className={`stat-value ${isRefreshing ? 'refreshing' : ''}`}>
                    {tokenData.totalSupply.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="stat-item">
                <span className="stat-label">Available to Community</span>
                <div className="stat-value-container">
                  <span className={`stat-value ${isRefreshing ? 'refreshing' : ''}`}>
                    {tokenData.tokensForSale.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="token-info-message mt-8 p-4 bg-blue-900 bg-opacity-30 rounded-lg">
              <h4 className="text-lg font-semibold text-blue-300 mb-2">Coming Soon!</h4>
              <p className="text-white">
                The YILDX token is not yet available. Join our waiting list to be notified when 
                the token goes live and get early access!
              </p>
            </div>

            <div className="token-benefits mt-6">
              <h4 className="text-lg font-semibold mb-3">Benefits of YILDX Token</h4>
              <ul className="list-disc list-inside space-y-2 text-white text-opacity-90">
                <li>Participate in governance of the YildX protocol</li>
                <li>Earn passive income from protocol revenues</li>
                <li>Gain exclusive access to governance features</li>
                <li>Help to shape the fund's future</li>
              </ul>
            </div>

            <div className="token-purchase-button mt-6">
              <Button 
                variant="secondary" 
                onClick={openMailingListPopup}
                className="bg-blue-600 hover:bg-blue-700 w-full"
              >
                Join Waiting List
              </Button>
            </div>
          </>
        )}
      </div>
    );
  };

  // Render active sale view (detailed)
  const renderActiveSaleView = () => {
    return (
      <div className="token-info-card">
        <div className="token-info-header">
          <h3 className="text-2xl font-semibold mb-6">Token Information</h3>
          <div className="token-symbol">YILDX</div>
        </div>

        {initialLoading ? (
          <div className="loading-spinner flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
          </div>
        ) : (
          <>
            <div className="token-stats">
              <div className="stat-item">
                <span className="stat-label">Total Supply</span>
                <div className="stat-value-container">
                  <span className={`stat-value ${isRefreshing ? 'refreshing' : ''}`}>
                    {tokenData.totalSupply.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="stat-item">
                <span className="stat-label">For Sale</span>
                <div className="stat-value-container">
                  <span className={`stat-value ${isRefreshing ? 'refreshing' : ''}`}>
                    {tokenData.tokensForSale.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="stat-item">
                <span className="stat-label">Current Price</span>
                <div className="stat-value-container">
                  <span className={`stat-value ${isRefreshing ? 'refreshing' : ''}`}>
                    {formatEthPrice(currentPrice)}
                  </span>
                </div>
              </div>
            </div>

            {/* Multi-tier visualization - only show if tiers exist */}
            {tiers.length > 0 && (
              <div className="mt-6 mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium">Price Tiers</span>
                  <span className={`text-sm ${isRefreshing ? 'refreshing' : ''}`}>
                    {getCurrentTierName()}
                  </span>
                </div>
                
                <div className="space-y-3">
                  {tierProgressData.map((tier) => (
                    <div key={`tier-${tier.tierNumber}`} className="tier-progress-container">
                      <div className="flex justify-between items-center mb-1 text-xs">
                        <span className={tier.isActive ? "font-bold text-yellow-300" : ""}>
                          {tier.isActive && "→ "}Tier {tier.tierNumber} ({tier.price})
                        </span>
                        <span>
                          {tier.soldInTier.toLocaleString()} / {tier.tierSize.toLocaleString()}
                        </span>
                      </div>
                      <div className="tier-progress-bar bg-gray-700 h-3 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all duration-500 ease-out ${
                            tier.isActive ? 'bg-yellow-500' : 'bg-blue-600'
                          }`}
                          style={{ width: `${tier.percentageInTier}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Overall sale progress */}
            <div className="sale-progress-container mt-8">
              <div className="sale-progress-label">
                <span>Overall Progress</span>
                <span className={isRefreshing ? 'refreshing' : ''}>
                  {Math.round(soldPercentage)}%
                </span>
              </div>
              <div className="sale-progress-bar">
                <div 
                  className="progress-bar-sold progress-transition" 
                  style={{ width: `${soldPercentage}%` }}
                ></div>
                <div 
                  className="progress-bar-remaining progress-transition" 
                  style={{ width: `${remainingPercentage}%` }}
                ></div>
              </div>
              <div className="sale-progress-details">
                <span className={isRefreshing ? 'refreshing' : ''}>
                  {tokenData.tokensSold.toLocaleString()} YILDX sold
                </span>
                <span className={isRefreshing ? 'refreshing' : ''}>
                  {(tokenData.tokensForSale - tokenData.tokensSold).toLocaleString()} YILDX available
                </span>
              </div>
            </div>

            {tokensUntilNextTier > 0 && (
              <div className="next-tier-info mt-3 p-3 bg-yellow-900 bg-opacity-30 rounded-lg text-yellow-300 text-sm">
                <span className="font-medium">Only {tokensUntilNextTier.toLocaleString()} tokens left</span> at current price!
                <br />
                Price increases in next tier.
              </div>
            )}

            <div className="token-purchase-info mt-4">
              <p className="text-sm">
                • Mint is active<br />
                • Governance tokens earn a share of protocol revenues<br />
                • Be first and mint more!
              </p>
            </div>

            {web3Connected ? (
              <div className="token-purchase-form mt-6">
                <div className="flex flex-col space-y-4">
                  <div className="flex flex-col">
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-sm font-medium">
                        {inputMode === "token" ? "Amount in YILDX" : "Amount in ETH"}
                      </label>
                      <button 
                        onClick={toggleInputMode}
                        className="text-sm text-blue-400 hover:text-blue-300"
                      >
                        Switch to {inputMode === "token" ? "ETH" : "YILDX"} input
                      </button>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={inputMode === "token" ? purchaseAmount : ethAmount}
                        onChange={handleInputChange}
                        placeholder={`Enter ${inputMode === "token" ? "YILDX" : "ETH"} amount`}
                        className="p-3 rounded-lg flex-grow text-black"
                        min="0.000001"
                        max={inputMode === "token" ? tokenData.maxPurchase : undefined}
                        disabled={isPurchasing}
                      />
                      <span className="text-white">{inputMode === "token" ? "YILDX" : "ETH"}</span>
                    </div>
                  </div>
                  
                  {inputMode === "token" && purchaseAmount ? (
                    <div className="eth-cost-display text-sm text-white text-opacity-90">
                      Estimated cost: {ethAmount} ETH
                    </div>
                  ) : inputMode === "eth" && ethAmount ? (
                    <div className="tokens-received-display text-sm text-white text-opacity-90">
                      You'll receive approximately: {purchaseAmount} YILDX
                    </div>
                  ) : null}
                  
                  <Button 
                    variant="primary"
                    onClick={handlePurchase}
                    className={isPurchasing ? "opacity-70 cursor-not-allowed" : ""}
                    disabled={isPurchasing}
                  >
                    {isPurchasing ? "Processing Purchase..." : "Purchase YILDX Tokens"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="token-purchase-button mt-6">
                <Button variant="primary" onClick={toggleWalletModal}>
                  Connect Wallet to Buy
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };
  
  return (
    <section className="token-sale-section px-5 py-32">
      <div className="mx-auto my-0 max-w-[1200px]">
        <h2 className="mb-12 text-5xl font-bold text-center text-white">
          YildX Governance Token
        </h2>
        <p className="mx-auto mb-16 text-xl leading-relaxed max-w-[800px] text-center text-white text-opacity-90">
          Participate in fund governance and earn passive income. Hold YildX tokens to gain voting rights and receive a share of all fund revenues including mint/burn fees and annual management fees.
        </p>

        <div className="token-info-container">
          {tokenData.initialSaleActive ? renderActiveSaleView() : renderInactiveSaleView()}
        </div>
        
        {/* Admin Panel - Only visible to owner */}
        {isOwner && (
          <TokenSaleAdmin 
            provider={provider}
            tokenContract={tokenContract}
            tokenData={tokenData}
            refreshTokenSaleData={refreshTokenSaleData}
            isRefreshing={isRefreshing}
            tiers={tiers}
            currentTier={currentTier}
          />
        )}
      </div>
      
      {/* Transaction Popup */}
      <TokenSalePopup
        isOpen={showTransactionPopup}
        onClose={() => setShowTransactionPopup(false)}
        onComplete={handlePurchaseComplete}
        onRefresh={refreshTokenSaleData}
        status={transactionStatus}
        purchaseAmount={purchaseAmount}
        ethCost={ethAmount}
        message={transactionMessage}
        txHash={transactionHash}
        provider={provider}
        tokenAddress={YILDX_TOKEN_ADDRESS}
        autoCloseTime={transactionStatus === 'success' ? 5000 : transactionStatus === 'failed' ? 8000 : 0}
      />
      
      {/* Token Reminder Popup */}
      <AddTokenReminderPopup
        isOpen={showTokenReminder}
        onClose={handleTokenReminderComplete}
        tokenAddress={YILDX_TOKEN_ADDRESS}
        tokenSymbol="YILDX"
        tokenDecimals={18}
        tokenImage="" // Add your token image URL here if available
      />
      
      {/* Mailing List Popup */}
      {showMailingListPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4 border border-blue-500 shadow-lg">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-white">Join Our Mailing List</h3>
              <button 
                onClick={() => setShowMailingListPopup(false)}
                className="text-gray-400 hover:text-white"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {subscribeSuccess ? (
              <div className="text-center py-6">
                <div className="mx-auto bg-green-900 rounded-full h-16 w-16 flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">Thank you!</h4>
                <p className="text-gray-300 mb-4">
                  You're on the list! We'll notify you as soon as the YildX is live.
                </p>
              </div>
            ) : (
              <>
                <p className="text-gray-300 mb-6">
                  Join our mailing list to be the first to know when we go live. Get first to know!
                </p>
                
                <form onSubmit={handleSubscribeToMailingList} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                      Email address*
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="w-full p-3 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                      Name (optional)
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="w-full p-3 rounded-lg bg-gray-700 border border-gray-600 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  {subscribeError && (
                    <div className="text-red-400 text-sm">{subscribeError}</div>
                  )}
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="consent"
                      required
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-500 rounded"
                    />
                    <label htmlFor="consent" className="ml-2 block text-xs text-gray-300">
                      I consent to receive email updates about YildX token sale and governance news
                    </label>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubscribing}
                    className={`w-full py-3 px-4 rounded-lg font-medium text-white ${
                      isSubscribing
                        ? "bg-blue-700 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-500 transition-colors"
                    }`}
                  >
                    {isSubscribing ? "Subscribing..." : "Join Waiting List"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default TokenSaleSection;